import React from 'react'
import { Handle, getSmoothStepPath, getMarkerEnd, EdgeText, getEdgeCenter } from 'react-flow-renderer'

/*
 * Read-only copies of the node and edge renderers from GraphDiagram.
 *
 * Deliberately a copy rather than a shared module: the builder is the content
 * team's daily tool and was left untouched. The difference from the originals is
 * that nothing here takes a click handler, an onConnect or a mutation ref, so a
 * snapshot canvas cannot open an editor or write to the live script.
 *
 * If a badge or colour is added to a node in GraphDiagram, mirror it here or the
 * snapshot view will quietly stop matching. Worth merging the two once the
 * snapshot canvas has settled.
 */

export const standardColors = ['peach', 'olive_green', 'purple', 'pink', 'aqua_blue', 'sky_blue', 'dark_pink', 'orange', 'aqua_green']

const foreignObjectSize = 20

//Mirrors the colourCodeOutcomes derivation in GraphDiagram: project outcomes
//plus the script's own headings, each given a colour by position.
export const buildColorCodeOutcomes = (outcomes = [], scriptHeadings) => {
    const combined = scriptHeadings && scriptHeadings.length > 0
        ? [...outcomes, ...scriptHeadings]
        : [...outcomes]

    return combined.map((entry, index) => ({
        title: entry.toLowerCase().includes('flow')
            ? entry
            : outcomes.includes(entry) ? `${entry} Flow` : entry,
        color: standardColors[index % standardColors.length]
    }))
}

export const getNodeColor = (nodeInfo, colorCodeOutcomes) => {
    const { heading } = nodeInfo.data
    const nodeHeading = colorCodeOutcomes.find(e => e.title == heading)
    if (nodeHeading) {
        return `${nodeHeading.color}_node`
    }

    switch (heading) {
        case 'Quick Pitch': return `quick_pitch_node`
        case 'Closing': return `closing_node`
        case 'Main Pitch': return `main_pitch_node`
        case 'Introduction': return `introduction_node`
        case 'Start': return `start_ending_node`
        case 'Ending': return `start_ending_node`
        default: return `aqua_blue_node`
    }
}

export const checkHeadingNodeClass = (nodeInfo, colorCodeOutcomes) => {
    const { heading } = nodeInfo.data
    const nodeHeading = colorCodeOutcomes.find(e => e.title == heading)
    if (nodeHeading) {
        return `heading-node-i ${nodeHeading.color}`
    }

    switch (heading) {
        case 'Closing': return `heading-node-i closing`
        case 'Quick Pitch': return `heading-node-i quick_pitch`
        case 'Introduction': return `heading-node-i introduction`
        case 'Main Pitch': return `heading-node-i main_pitch`
        case 'Start': return `heading-node-i start_end`
        case 'Ending': return `heading-node-i start_end`
        default: return `heading-node-i aqua_blue`
    }
}

const getEdgeBorderColor = (target, elements, colorCodeOutcomes) => {
    const targetNode = elements.filter(e => e.id == target)[0]
    const node = targetNode && targetNode.data && colorCodeOutcomes.find(e => e.title == targetNode.data.heading)

    switch (node && node.color) {
        case 'peach': return `#BE5000`
        case 'olive_green': return `#809F00`
        case 'purple': return `#415ED8`
        case 'pink': return `#CE16BE`
        case 'aqua_blue': return `#1CADAD`
        case 'sky_blue': return `#023349`
        case 'dark_pink': return `#7B0ABE`
        case 'orange': return `#EE9310`
        case 'aqua_green': return `#1BAF7E`
        default: return `black`
    }
}

const getColorForEdge = (target, elements, colorCodeOutcomes) => {
    const targetNode = elements.filter(e => e.id == target)[0]
    const heading = targetNode && targetNode.data && targetNode.data.heading
    const match = colorCodeOutcomes.find(e => e.title == heading)

    if (match) return `react-flow__edge-path ${match.color}`

    switch (heading) {
        case 'Introduction': return `react-flow__edge-path introduction`
        case 'Closing': return `react-flow__edge-path closing`
        case 'Quick Pitch': return `react-flow__edge-path quick_pitch`
        case 'Main Pitch': return `react-flow__edge-path main_pitch`
        default: return `react-flow__edge-path`
    }
}

//The badge row is identical across all three node types in the builder.
const NodeBody = ({ data, elementsRef, colorRef }) => {
    const colorCodeOutcomes = colorRef.current
    const found = elementsRef.current.find(e => e.id == data.id)

    return <>
        <div className={checkHeadingNodeClass(data, colorCodeOutcomes)}>
            <span>{data.data.heading}</span>
            {found && found.initialNode ? <span style={{ fontStyle: 'italic' }}>(Primary node)</span> : null}
            {found && found.initialNode ? <img src={require('../../../../assets/images/Icons_dark_star.svg')} /> : null}
            {data.data.audio ? <img src={require('../../../../assets/images/Interactive Script/Icons_audio_bold.svg')} /> : null}
        </div>
        <div className='text'>{data.data.label}</div>
        <div className="node-attachment-wrapper">
            {data.data.actions && data.data.actions.length > 0
                ? <div className="trigger-action"><img src={require('../../../../assets/images/Icons_IS_trigger_white.svg')} />Trigger Action</div>
                : null}
            {data.data.callTransfer ? <div className="call-transfer-box"><img src={require('../../../../assets/images/Icons_IS_call_transfer_white.svg')} />Call Transfer</div> : null}
            {data.data.form && data.data.form.length > 0 ? <div className="data-collection-box">Form</div> : null}
            {data.data.callerCueText ? <div className="caller-cue-box">{data.data.callerCueText}</div> : null}
        </div>
    </>
}

/*
 * Takes refs, not values. ReactFlow caches these components from the first
 * render, so a closed-over array stays empty forever - which silently loses the
 * initial-node star and the whole colour map. GraphDiagram uses mutationRef for
 * the same reason.
 */
export const makeNodeTypes = ({ elementsRef, colorRef, onNodeClick }) => {
    const handleStyle = { borderRadius: 0, height: 10, width: 10 }
    const shared = { elementsRef, colorRef }

    return {
        input: (data) => (
            <div className={getNodeColor(data, colorRef.current)} onClick={() => onNodeClick && onNodeClick(data)}>
                <Handle type="source" position="bottom" style={handleStyle} isConnectable={false} />
                <NodeBody data={data} {...shared} />
            </div>
        ),
        output: (data) => (
            <div className={getNodeColor(data, colorRef.current)} onClick={() => onNodeClick && onNodeClick(data)}>
                <Handle type="target" position="top" style={handleStyle} isConnectable={false} />
                <NodeBody data={data} {...shared} />
            </div>
        ),
        default: (data) => (
            <div className={getNodeColor(data, colorRef.current)}>
                <div onClick={() => onNodeClick && onNodeClick(data)}>
                    <Handle type="source" position="bottom" style={handleStyle} isConnectable={false} />
                    <Handle type="target" position="top" style={handleStyle} isConnectable={false} />
                    <NodeBody data={data} {...shared} />
                </div>
            </div>
        )
    }
}

export const makeEdgeTypes = ({ elementsRef, colorRef, onEdgeClick }) => ({
    step: ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition,
        style, data, source, target, arrowHeadType = 'arrowclosed', label, animated, markerEndId }) => {

        const edgePath = getSmoothStepPath({
            sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius: 15
        })
        const markerEnd = getMarkerEnd(arrowHeadType, markerEndId)
        const [centerX, centerY] = getEdgeCenter({ sourceX, sourceY, targetX, targetY })

        return <>
            <path id={id}
                style={{ ...style, strokeWidth: '3.5px' }}
                className={getColorForEdge(target, elementsRef.current, colorRef.current)}
                d={edgePath}
                markerEnd={markerEnd}
                onClick={() => onEdgeClick && onEdgeClick({ id, label, source, target, data, style, animated })} />
            <EdgeText
                x={centerX}
                y={centerY}
                label={<tspan>{(data && data.outcome) || label}</tspan>}
                labelStyle={{ fontSize: '12px', fill: getEdgeBorderColor(target, elementsRef.current, colorRef.current) }}
            />
            <foreignObject
                width={foreignObjectSize}
                height={foreignObjectSize}
                x={centerX - foreignObjectSize / 2}
                y={centerY - foreignObjectSize / 16}
                className="edgebutton-foreignobject"
                requiredExtensions="http://www.w3.org/1999/xhtml"
            >
                {data && data.audio
                    ? <img src={require('../../../../assets/images/Interactive Script/Icons_audio_bold.svg')} style={{ width: '12px', height: '12px' }} />
                    : null}
            </foreignObject>
        </>
    }
})
